/*
// 
// FEIN (optional)
// License # (if applicable)


 * EASY_CONTRACTOR_Insurance_LandingPage_v03 (React + TypeScript + React-Bootstrap)
 * Date: 2025-09-17 (America/Denver)
 * 
 * Highlights:
 * - Schema-driven sections & fields (super easy to add/remove/reorder)
 * - Zip-gate mini step → reveals full form (wizard or vertical mode)
 * - Central validators, touched map, and payload builder
 * - Reusable <FormBuilder/> that renders any field types
 * - Swappable CSS reveal effects
 *
 * Notes:
 * - Replace API_URL with your endpoint
 * - Update brand copy, images, FAQs and carriers component as desired
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";

/* Optional: drop in your existing shared components
// import { FAQ_Component_v02b_Simple } from "../component-library/FAQ_Component_v02b-simple";
// import { contractorFAQ_CONTENT_v01 } from "../component-demos/Contractor-Insurance/FAQ_CONTENT_v01";
// import InsuranceCarriersList_v01 from "../Insurance-Legacy/InsuranceCarriersList_v01";
// import { MetaTags } from "../component-library/UTILITY-COMPONENTS/META-TAGS";
*/

/* ============================================================
   CONFIG
============================================================ */
const VERSION_NUMBER = "v03b";
const SHOW_VERSION = true;
const TEST_MODE = true;
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback"; // <-- replace
const imagePath = "insurance/images/Contractor-Insurance/";
const ImageInterval = 6500;

const BRAND = {
  company: "Easy Contractor Insurance",
  short: "Easy Contractor",
};

const AGENT_PHONE_NUMBER = "(303) 337-4433";
const AGENT_PHONE_HOURS = "M-F  9am to 5pm MT";
const AGENT_PHONE_BUTTON = (
  <>
    <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
    <OverlayTrigger placement="right" overlay={<Tooltip id="AgentHoursTip">{AGENT_PHONE_HOURS}</Tooltip>}>
      <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
    </OverlayTrigger>
  </>
);

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const digitsOnly = (s: string) => (s || "").replace(/\D+/g, "");
const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
const isValidPhone = (raw: string) => digitsOnly(raw).length === 10;
const isValidZipCode = (raw: string) => digitsOnly(raw).length === 5;
const formatPhonePretty = (raw: string) => {
  const d = digitsOnly(raw);
  return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : raw;
};

/* ============================================================
   FIELD SCHEMA
============================================================ */
// 1) Field types
export type FieldType =
  | "text" | "email" | "phone" | "zip" | "number" | "date"
  | "select" | "checkbox" | "switch" | "textarea";

// 2) Field definition
export interface FieldDef {
  name: string;                // key in the form state
  label: string;               // UI label
  type: FieldType;             // input type
  placeholder?: string;
  options?: { value: string; label: string }[]; // for select
  helperText?: string;
  subLabel?: string;           // NEW: additional info under the control
  required?: boolean;
  col?: number;                // bootstrap column (1-12)
  showIf?: (values: Record<string, any>) => boolean; // conditional display
  normalize?: (v: any) => any; // onChange transform
  validate?: (values: Record<string, any>) => boolean; // validator uses full values
}

// 3) Section definition
export interface ISectionDef {
  id: string;
  title: string;
  subTitle?:string
  description?: string;
  fields: FieldDef[];
  showIf?: (values: Record<string, any>) => boolean;
}

/* ============================================================
   REUSABLE FORM BUILDER
============================================================ */
interface FormBuilderProps {
  sections: ISectionDef[];
  values: Record<string, any>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  touched: Record<string, boolean>;
  markTouched: (name: string) => void;
}

const vp = (
  name: string,
  pass: boolean,
  touched: Record<string, boolean>
) => ({
  onBlur: () => void 0,
  isValid: !!touched[name] && pass,
  isInvalid: !!touched[name] && !pass,
  className: !!touched[name] && pass ? "bg-success-subtle" : undefined,
});

const FormBuilder: React.FC<FormBuilderProps> = ({ sections, values, setValues, touched, markTouched }) => {
  const handleChange = (name: string, value: any, normalize?: (v:any)=>any) => {
    setValues((prev) => ({ ...prev, [name]: normalize ? normalize(value) : value }));
  };

  const validators: Record<string, (vals: Record<string, any>) => boolean> = {};
  sections.forEach(sec => sec.fields.forEach(f => {
    if (f.validate) validators[f.name] = f.validate;
    else if (f.required) {
      validators[f.name] = (vals) => {
        const v = vals[f.name];
        if (f.type === "checkbox" || f.type === "switch") return !!v;
        return v !== undefined && v !== null && String(v).trim().length > 0;
      };
    }
  }));

  const V = Object.fromEntries(Object.keys(validators).map(k => [k, validators[k](values)]));

  const renderField = (f: FieldDef) => {
    const visible = f.showIf ? f.showIf(values) : true;
    if (!visible) return null;

    // Treat TRUE/FALSE questions as CHECKBOXES (switch behaves as checkbox)
    const isBoolField = f.type === "checkbox" || f.type === "switch";
    const effectiveType = isBoolField ? "checkbox" : f.type;

    const common = {
      id: f.name,
      value: values[f.name] ?? (isBoolField ? false : ""),
      onChange: (e: any) =>
        handleChange(
          f.name,
          e?.target?.type === "checkbox" ? e.target.checked : e.target.value,
          f.normalize
        ),
      placeholder: f.placeholder,
    } as any;

    const validPass = f.validate
      ? f.validate(values)
      : (f.required ? !!(values[f.name] && String(values[f.name]).trim()) : true);

    return (
      <Col md={f.col ?? 4} key={f.name}>
        {/* Avoid double labels for checkboxes */}
        {!isBoolField && (
          <Form.Label htmlFor={f.name}>
            {f.label}{f.required ? " *" : ""}
          </Form.Label>
        )}

        {effectiveType === "text" && (
          <>
            <Form.Control {...common} required={f.required} {...vp(f.name, validPass, touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "email" && (
          <>
            <Form.Control type="email" {...common} required={f.required} {...vp(f.name, isValidEmail(values[f.name] || ""), touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "phone" && (
          <>
            <Form.Control inputMode="tel" {...common} required={f.required} {...vp(f.name, isValidPhone(values[f.name] || ""), touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "zip" && (
          <>
            <Form.Control
              inputMode="numeric"
              maxLength={5}
              {...common}
              onChange={(e:any)=> handleChange(f.name, digitsOnly(e.target.value).slice(0,5), f.normalize)}
              required={f.required}
              {...vp(f.name, isValidZipCode(values[f.name] || ""), touched)}
            />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "number" && (
          <>
            <Form.Control inputMode="numeric" type="number" {...common} required={f.required} {...vp(f.name, !!String(values[f.name] ?? "").trim(), touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "date" && (
          <>
            <Form.Control type="date" {...common} required={f.required} {...vp(f.name, !!values[f.name], touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "select" && (
          <>
            <Form.Select {...common} required={f.required} {...vp(f.name, !!values[f.name], touched)}>
              <option value="">Select...</option>
              {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "checkbox" && (
          <>
            <Form.Check
              type="checkbox"
              id={f.name}
              label={<>{f.label}{f.required ? " *" : ""}</>}
              checked={!!values[f.name]}
              onChange={(e:any)=> handleChange(f.name, e.target.checked, f.normalize)}
            />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}

        {effectiveType === "textarea" && (
          <>
            <Form.Control as="textarea" rows={3} {...common} required={f.required} {...vp(f.name, !!values[f.name] || !f.required, touched)} />
            {f.subLabel && <div className="text-muted small mt-1">{f.subLabel}</div>}
            {f.helperText && <div className="form-text">{f.helperText}</div>}
          </>
        )}
      </Col>
    );
  };

  return (
    <>
      {sections.map((sec) => {
        const sectionVisible = sec.showIf ? sec.showIf(values) : true;
        if (!sectionVisible) return null;
        return (
          <Col xs={12} key={sec.id}>
            <fieldset className="border rounded-3 p-3 bg-body-secondary">
              <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                <strong>{sec.title}</strong>
                <p>{sec.subTitle}</p>
              </legend>
              {sec.description && <p className="mb-3 text-body-secondary">{sec.description}</p>}
              <Row className="g-3 mt-1">
                {sec.fields.map(renderField)}
              </Row>
            </fieldset>
          </Col>
        );
      })}
    </>
  );
};

/* ============================================================
   SECTION SCHEMA (EASY TO EDIT)
============================================================ */
const CONTACT_SECTION: ISectionDef = {
  id: "contact",
  title: "Contact & Location",
  fields: [
    { name: "fullName", label: "Full Name", type: "text", placeholder: "Jane Contractor", required: true, col: 6 },
    { name: "email", label: "Email", type: "email", placeholder: "you@example.com", required: true, col: 6 },
    { name: "phone", label: "Phone", type: "phone", placeholder: "(555) 123-4567", required: true, col: 4, subLabel: "Mobile preferred for fastest follow-up." },
    { name: "state", label: "State", type: "select", required: true, col: 4, options: US_STATES.map(s=>({value:s.code,label:s.name})) },
    { name: "zipCode", label: "Zip Code", type: "zip", required: true, col: 4, subLabel: "Used to match local rates and availability." },
  ],
};

const BUSINESS_SECTION: ISectionDef = {
  id: "business",
  title: "Business Basics",
  description: "Tell us about your contracting business so we can match the right coverage.",
  fields: [
    { name: "legalName", label: "Business Name", type: "text", placeholder: "ABC Plumbing LLC", required: true, col: 6 },
    { name: "trade", label: "Primary Trade", type: "select", required: true, col: 6, options: [
      "General Contractor", "Sub Contractor", "Plumber","Electrician","HVAC","Painter","Roofer","Landscaper","Carpenter","Handyman","Concrete","Masonry","Flooring","Drywall","Other"
    ].map(t=>({value:t,label:t})) },
    // { name: "entityType", label: "Entity Type", type: "select", required: true, col: 4, options: ["LLC","Corp","Sole Prop","Partnership","Other"].map(x=>({value:x,label:x})) },
    { name: "yearsInBusiness", label: "Years in Business", type: "number", placeholder: "e.g., 5", required: true, col: 4 },
    { name: "employees", label: "# of Employees", type: "number", placeholder: "e.g., 3", required: true, col: 4, subLabel: "Include owners on payroll if applicable." },
    // { name: "fein", label: "FEIN (optional)", type: "text", col: 4, required: false },
    // { name: "licenseNumber", label: "License # (if applicable)", type: "text", col: 4 },
    { name: "annualRevenue", label: "Annual Revenue", type: "text", placeholder: "$250,000", col: 4, subLabel: "Estimate is fine — rough numbers okay." },
  ],
};

const COVERAGE_SECTION: ISectionDef = {
  id: "coverage",
  title: "Coverage Needed",
  subTitle: "Your agent will help with these questions - All good if you have questions. Also, this can all be changed as needed.",
  fields: [
    { name: "needGL", label: "General Liability", type: "checkbox", col: 4 },
    { name: "needWC", label: "Workers' Compensation", type: "checkbox", col: 4, showIf: (v)=> Number(v.employees||0) > 0 },
    { name: "needCA", label: "Commercial Auto", type: "checkbox", col: 4 },
    { name: "needTools", label: "Tools & Equipment", type: "checkbox", col: 4 },
    { name: "needBond", label: "Contractor Bond", type: "checkbox", col: 4 },
    { name: "needUmbrella", label: "Umbrella / Excess", type: "checkbox", col: 4 },
    { name: "aiReq", label: "Additional Insured required?", type: "checkbox", col: 6, subLabel: "For GC or project requirements." },
    { name: "certReq", label: "Certificates required?", type: "checkbox", col: 6, subLabel: "COIs for jobs, landlords, or vendors." },
    { name: "projType", label: "Typical Project Type", type: "checkbox", col: 6, options: [
      "Residential","Commercial","Municipal","Industrial","Mixed"
    ].map(x=>({value:x,label:x})) },
    { name: "avgJobSize", label: "Average Job Size", type: "text", placeholder: "$10,000", col: 6, subLabel: "Ballpark is okay." },
  ],
};

const LOSS_HISTORY_SECTION: ISectionDef = {
  id: "lossHistory",
  title: "Loss & Safety",
  fields: [
    { name: "anyClaims", label: "Any claims in last 3 years?", type: "checkbox", col: 6 },
    { name: "claimsCount", label: "# of claims", type: "number", col: 3, showIf: (v)=>!!v.anyClaims },
    { name: "claimsNotes", label: "Brief details regarding claims in the last 3 years (optional)", type: "textarea", col: 12, showIf: (v)=>!!v.anyClaims, subLabel: "Dates and short description help speed things up." },
    { name: "safetyProgram", label: "Written safety program?", type: "checkbox", col: 6 },
  ],
};

const NOTES_SECTION: ISectionDef = {
  id: "notes",
  title: "Notes (optional)",
  fields: [
    { name: "notes", label: "Other helpful information about your business?", type: "textarea", col: 12, subLabel: "Scheduling needs, certificates, endorsements, etc." },
  ],
};

// Assemble the full schema here (reorder freely)
const ALL_SECTIONS: ISectionDef[] = [
  CONTACT_SECTION,
  BUSINESS_SECTION,
  COVERAGE_SECTION,
  LOSS_HISTORY_SECTION,
  NOTES_SECTION,
];

// Define wizard steps by section ids for quick control
const WIZARD_FLOW = ["contact", "business", "coverage", "lossHistory", "notes", "confirm"] as const;

/* ============================================================
   EFFECTS (easy to swap)
============================================================ */
const FX_ZIP = "fx-reveal fade-slide-up";
const FX_FORM = "fx-reveal fade-scale-in";

/* ============================================================
   MAIN COMPONENT
============================================================ */
export const EASY_CONTRACTOR_LandingPage_v03a: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const zipRef = useRef<HTMLDivElement | null>(null);

  const [activeSlide, setActiveSlide] = useState(0);
  const [showZipGate, setShowZipGate] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState<string | null>(null);

  // schema-driven values store
  const [values, setValues] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  // mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);

  // submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // build a validators map from schema
  const validators: Record<string, (vals: Record<string, any>) => boolean> = {};
  ALL_SECTIONS.forEach(sec => sec.fields.forEach(f => {
    if (f.validate) validators[f.name] = f.validate;
    else if (f.required) {
      validators[f.name] = (vals) => {
        const v = vals[f.name];
        if (f.type === "phone") return isValidPhone(v || "");
        if (f.type === "email") return isValidEmail(v || "");
        if (f.type === "zip") return isValidZipCode(v || "");
        if (f.type === "checkbox" || f.type === "switch") return !!v;
        return v !== undefined && v !== null && String(v).trim().length > 0;
      };
    }
  }));

  const isValidField = (name: string, vals: Record<string, any>) => validators[name] ? validators[name](vals) : true;

  const sectionById = (id: string) => ALL_SECTIONS.find(s => s.id === id)!;

  // derive overall validity
  const formValid = useMemo(() => {
    // Only require fields for visible sections
    return ALL_SECTIONS.every(sec => {
      const visible = sec.showIf ? sec.showIf(values) : true;
      if (!visible) return true;
      return sec.fields.every(f => {
        // visible field?
        if (f.showIf && !f.showIf(values)) return true;
        if (!f.required) return true;
        return isValidField(f.name, values);
      });
    });
  }, [values]);

  const isStepValid = (stepIdx: number) => {
    const id = WIZARD_FLOW[stepIdx];
    if (!id || id === "confirm") return submitOk;
    const sec = sectionById(id);
    const visible = sec.showIf ? sec.showIf(values) : true;
    if (!visible) return true;
    return sec.fields.every(f => {
      if (f.showIf && !f.showIf(values)) return true;
      return !f.required || isValidField(f.name, values);
    });
  };

  const isPhoneViewport = () => (typeof window !== "undefined") && ((window.matchMedia?.("(max-width: 767.98px)")?.matches) || window.innerWidth < 768);
  const smartScrollTo = (el: HTMLElement | null) => {
    if (typeof window === "undefined") return;
    if (isPhoneViewport()) el?.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const revealZipGate = () => {
    setShowForm(false);
    setShowZipGate(true);
    setTimeout(() => smartScrollTo(zipRef.current!), 50);
  };

  const handleZipOk = () => {
    const cleaned = digitsOnly(zipInput).slice(0,5);
    if (cleaned.length !== 5) { setZipError("Please enter a valid 5-digit zip code."); return; }
    setZipError(null);
    setValues(v => ({...v, zipCode: cleaned}));
    setShowZipGate(false);
    setShowForm(true);
    setTouched(t => ({...t, zipCode: true}));
    setTimeout(() => smartScrollTo(formRef.current!), 50);
  };

  const goNext = () => {
    if (!isStepValid(currentStep)) return;
    setCurrentStep(s => Math.min(WIZARD_FLOW.length - 1, s + 1));
  };
  const goBack = () => setCurrentStep(s => Math.max(0, s - 1));

  const markSectionTouched = (secId: string) => {
    const sec = sectionById(secId);
    setTouched(t => sec.fields.reduce((acc, f) => ({...acc, [f.name]: true}), t));
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && WIZARD_FLOW[currentStep] !== "confirm") {
      markSectionTouched(WIZARD_FLOW[currentStep]);
      if (isStepValid(currentStep)) setCurrentStep(s => s + 1);
      return;
    }

    // mark all visible as touched
    setTouched(t => ALL_SECTIONS.reduce((acc, sec) => {
      const visible = sec.showIf ? sec.showIf(values) : true;
      if (!visible) return acc;
      sec.fields.forEach(f => acc[f.name] = true);
      return acc;
    }, {...t} as Record<string, boolean>));

    if (!formValid) { setSubmitError("Please complete all required fields."); return; }

    // Build payload
    const payload = buildPayload(values);

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitOk(false);
      setServerStatus(null);

      if (TEST_MODE) {
        await new Promise(r => setTimeout(r, 700));
        setServerStatus({ ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2,8) });
        setSubmitOk(true);
        if (isWizard) setCurrentStep(WIZARD_FLOW.length - 1);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });
      let json: any = null; try { json = await res.json(); } catch {}
      if (!res.ok) {
        const msg = (json && (json.error || json.message)) || `Request failed: ${res.status}`;
        setServerStatus({ ok: false, status: res.status, message: msg });
        throw new Error(msg);
      }
      setServerStatus({ ok: true, status: res.status, id: json?.id || json?.requestId });
      setSubmitOk(true);
      if (isWizard) setCurrentStep(WIZARD_FLOW.length - 1);
    } catch (err:any) {
      setSubmitError(err?.message || String(err));
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  const startNewQuote = () => {
    setValues({}); setTouched({}); setSubmitOk(false); setServerStatus(null);
    setCurrentStep(0); setShowForm(false); setShowZipGate(false); setZipInput("");
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ConfirmationBlock: React.FC = () => (
    <fieldset className="border rounded-3 p-3 bg-body-secondary">
      <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
        <strong>Confirmation</strong>
      </legend>
      {submitOk ? (
        <>
          <Alert variant="success" className="mb-3">
            <div className="fw-semibold mb-1">Thank you! A representative will contact you shortly.</div>
            <div className="small mb-0">
              {serverStatus?.ok ? <>Server status: <strong>OK</strong>{serverStatus?.status ? ` • ${serverStatus.status}` : ""}{serverStatus?.id ? ` • ID: ${serverStatus.id}` : ""}</> : "Server status: Unknown"}
            </div>
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Contact</Card.Header>
                <Card.Body className="small">
                  <div><strong>Name:</strong> {values.fullName}</div>
                  <div><strong>Email:</strong> {values.email}</div>
                  <div><strong>Phone:</strong> {formatPhonePretty(values.phone || "")}</div>
                  <div><strong>State/Zip:</strong> {values.state} {values.zipCode}</div>
                  <div><strong>Business:</strong> {values.legalName}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Coverage</Card.Header>
                <Card.Body className="small">
                  <div><strong>GL / WC / Auto:</strong> {values.needGL ? "GL ✓" : "GL ✗"} / {values.needWC ? "WC ✓" : "WC ✗"} / {values.needCA ? "CA ✓" : "CA ✗"}</div>
                  <div><strong>Tools / Bond / Umbrella:</strong> {values.needTools ? "✓" : "✗"} / {values.needBond ? "✓" : "✗"} / {values.needUmbrella ? "✓" : "✗"}</div>
                  <div><strong>AI / Certs:</strong> {values.aiReq ? "✓" : "✗"} / {values.certReq ? "✓" : "✗"}</div>
                </Card.Body>
              </Card>
            </Col>
            {values.notes && (
              <Col md={12}>
                <Card>
                  <Card.Header className="fw-semibold">Notes</Card.Header>
                  <Card.Body className="small">{values.notes}</Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={startNewQuote}>Start New Quote</Button>
          </div>
        </>
      ) : (
        <Alert variant="warning">We couldn’t confirm the server response. Please go back and try submitting again.</Alert>
      )}
    </fieldset>
  );

  const active = HERO_SLIDES[activeSlide];

  return (
    <>
      {/* Optional meta tags */}
      {/* <MetaTags
        title={`${BRAND.company} – Fast, Affordable Coverage for Contractors`}
        description="Get contractor insurance quotes in minutes. General Liability, Workers' Comp, Commercial Auto, Tools & Equipment, and more."
        canonicalUrl="https://www.easycontractorinsurance.com/"
        imageUrl="https://www.easycontractorinsurance.com/images/og-image.jpg"
      /> */}

      <div className="bg-body">
        {/* Swappable effect CSS */}
        <style>{`
          .fx-reveal { --fx-dur: .45s; animation-duration: var(--fx-dur); animation-fill-mode: both; }
          .fade-slide-up { animation-name: fadeSlideUp; }
          .fade-scale-in { animation-name: fadeScaleIn; }
          @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
          @keyframes fadeScaleIn { from { opacity: 0; transform: scale(.98) } to { opacity: 1; transform: scale(1) } }
        `}</style>

        {/* Topbar */}
        <Navbar expand="md" bg="light" className="border-bottom sticky-top">
          <Container>
            <Navbar.Brand href="#" className="fw-bold">
              Easy <span className="text-primary">Contractor</span> Insurance
              {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION_NUMBER}</Badge>}
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="topbar" />
            <Navbar.Collapse id="topbar" className="justify-content-end">
              <Nav className="align-items-center gap-2">
                {AGENT_PHONE_BUTTON}
                <Button onClick={revealZipGate}>Get a Quote</Button>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        {/* HERO (simple illustrated) */}
        <section id="home" className="position-relative overflow-hidden" style={{ minHeight: 720 }}>
          <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
            {/* Use your own carousel or a single static hero image */}
            <div
              role="img"
              aria-label={active.alt}
              style={{
                width: "100%",
                height: "100%",
                minHeight: 720,
                backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${active.src}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          {/* Foreground content */}
          <Container className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-start g-0 g-sm-4 p-0 p-sm-4 p-lg-5">
              <Col lg={6} className="text-white">
                <div className="pe-lg-4" style={{ marginTop: 320 }}>
                  <h1 className="display-5 fw-bold">{active.title}</h1>
                  <p className="lead mb-3">{active.subline}</p>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Badge bg="light" text="dark">General Liability</Badge>
                    <Badge bg="light" text="dark">Workers' Comp</Badge>
                    <Badge bg="light" text="dark">Commercial Auto</Badge>
                  </div>
                  <div className="mt-4 d-flex gap-2">
                    <Button variant="light" onClick={revealZipGate}>Start Your Quote</Button>
                  </div>
                </div>
              </Col>

              {/* Right column: Zip Gate → Full Form */}
              <Col lg={6}>
                {/* ZIP GATE */}
                {showZipGate && (
                  <div ref={zipRef} className={FX_ZIP}>
                    <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                      <Card.Header className="bg-white">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="fw-semibold">Start Your Quote</div>
                          <Badge bg="success">Secure</Badge>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-4">
                        {zipError && (
                          <Alert variant="danger" onClose={() => setZipError(null)} dismissible>
                            {zipError}
                          </Alert>
                        )}
                        <Form onSubmit={(e)=>{ e.preventDefault(); handleZipOk(); }}>
                          <Form.Label htmlFor="zipGate">Enter Your Zip Code</Form.Label>
                          <InputGroup className="mb-2">
                            <Form.Control
                              id="zipGate"
                              inputMode="numeric"
                              maxLength={5}
                              placeholder="#####"
                              value={zipInput}
                              onChange={(e)=> setZipInput(digitsOnly(e.target.value).slice(0,5))}
                              autoFocus
                            />
                            <Button type="submit" variant="primary" disabled={digitsOnly(zipInput).length !== 5}>OK</Button>
                          </InputGroup>
                        </Form>
                      </Card.Body>
                    </Card>
                  </div>
                )}

                {/* FULL FORM */}
                {showForm && (
                  <div className={FX_FORM}>
                    <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                      <Card.Header className="bg-white">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <div className="fw-semibold">Fast, No-Obligation Quote</div>
                            <small className="text-body-secondary">{BRAND.company}</small>
                          </div>
                          <Badge bg="success">Secure</Badge>
                        </div>
                      </Card.Header>
                      <Card.Body ref={formRef}>
                        {submitError && (
                          <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                            {submitError}
                          </Alert>
                        )}

                        {/* MODE TOGGLE + (Wizard) Stepper
                            Note: This is a UI toggle (not a question), so we keep the visual "switch". */}
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="switch"
                              id="modeSwitch"
                              label={isWizard ? "Wizard mode" : "Vertical mode"}
                              checked={isWizard}
                              onChange={(e) => {
                                setMode(e.currentTarget.checked ? "wizard" : "vertical");
                                setTouched({});
                                if (submitOk && e.currentTarget.checked) setCurrentStep(WIZARD_FLOW.length - 1);
                                else setCurrentStep(0);
                              }}
                            />
                            <small className="text-muted">Switch to see all fields at once.</small>
                          </div>
                          <div style={{ minWidth: 280 }}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? WIZARD_FLOW.length : (submitOk ? 2 : 1)}</Badge>
                              <small className="text-muted">{isWizard ? (WIZARD_FLOW[currentStep] || "confirm") : (submitOk ? "Confirmation" : "All fields")}</small>
                            </div>
                            <ProgressBar now={isWizard ? ((currentStep + 1) / WIZARD_FLOW.length) * 100 : (submitOk ? 100 : 50)} />
                          </div>
                        </div>

                        {/* ---------- FORM BODY ---------- */}
                        <Form
                          onSubmit={handleSubmit}
                          noValidate
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && isWizard) {
                              const id = WIZARD_FLOW[currentStep];
                              if (id && id !== "confirm") { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }
                            }
                          }}
                        >
                          {isWizard ? (
                            <Row className="g-3">
                              {WIZARD_FLOW[currentStep] !== "confirm" ? (
                                <FormBuilder
                                  sections={[sectionById(WIZARD_FLOW[currentStep]) ]}
                                  values={values}
                                  setValues={(u)=>{ setValues(u); }}
                                  touched={touched}
                                  markTouched={markTouched}
                                />
                              ) : (
                                <Col xs={12}><ConfirmationBlock /></Col>
                              )}

                              {/* Wizard controls */}
                              {WIZARD_FLOW[currentStep] !== "confirm" && (
                                <div className="d-flex justify-content-between mt-2">
                                  <Button type="button" variant="outline-secondary" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); goBack(); }} disabled={currentStep === 0}>Previous</Button>
                                  <div className="d-flex gap-2">
                                    {currentStep < WIZARD_FLOW.length - 2 ? (
                                      <Button type="button" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); markSectionTouched(WIZARD_FLOW[currentStep]); if (isStepValid(currentStep)) goNext(); }} disabled={!isStepValid(currentStep)}>Next</Button>
                                    ) : (
                                      <Button type="submit" variant="primary" size="lg" disabled={submitting || !formValid}>
                                        {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </Row>
                          ) : (
                            // Vertical mode: render all sections at once
                            <>
                              <Row className="g-3">
                                <FormBuilder
                                  sections={ALL_SECTIONS}
                                  values={values}
                                  setValues={setValues}
                                  touched={touched}
                                  markTouched={markTouched}
                                />
                              </Row>

                              {!submitOk ? (
                                <div className="d-grid mt-3">
                                  <Button type="submit" variant="primary" size="lg" disabled={!formValid || submitting}>
                                    {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                                  </Button>
                                  <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                    By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                                  </div>
                                </div>
                              ) : (
                                <ConfirmationBlock />
                              )}
                            </>
                          )}
                        </Form>
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        {/* How It Works */}
        <section className="py-5 bg-light border-top border-bottom">
          <Container>
            <Row className="mb-4">
              <Col lg={8}>
                <h2 className="fw-bold mb-1">How It Works</h2>
                <p className="text-body-secondary mb-0">Three simple steps to get covered.</p>
              </Col>
            </Row>
            <Row className="g-4">
              <Col md={4}><StepCard n={1} title="Tell us about your business" text="Share basics about your trade and coverage needs." /></Col>
              <Col md={4}><StepCard n={2} title="We compare options" text="We shop multiple carriers for the best fit and price." /></Col>
              <Col md={4}><StepCard n={3} title="You choose and bind" text="Pick the plan you like and get certificates fast." /></Col>
            </Row>
          </Container>
        </section>

        {/* Features & Benefits */}
        <section className="py-5">
          <Container>
            <Row className="mb-4">
              <Col lg={8}>
                <h2 className="fw-bold mb-1">Features & Benefits</h2>
                <p className="text-body-secondary mb-0">Everything you need to win jobs and stay protected.</p>
              </Col>
            </Row>
            <Row className="g-4">
              <Col md={6} lg={3}><Feature emoji="⚡" title="Same-Day Certificates" text="COIs issued fast so you can start work sooner." /></Col>
              <Col md={6} lg={3}><Feature emoji="🛡️" title="Right-Sized Coverage" text="GL, WC, Auto, Tools, Bond—tailored to your trade." /></Col>
              <Col md={6} lg={3}><Feature emoji="💬" title="Human Help" text="Talk to licensed advisors who know contractors." /></Col>
              <Col md={6} lg={3}><Feature emoji="💳" title="Flexible Payments" text="Competitive options from multiple carriers." /></Col>
            </Row>
          </Container>
        </section>

        {/* FAQ (optional) */}
        {/*
        <section id="faq" className="py-5 bg-light border-top">
          <Container>
            <Row className="mb-4">
              <Col lg={8}>
                <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
                <p className="text-body-secondary mb-0">Short answers to common questions.</p>
              </Col>
            </Row>
            <FAQ_Component_v02b_Simple faqs={contractorFAQ_CONTENT_v01} openFirst />
          </Container>
        </section>
        */}

        {/* Carrier list (optional) */}
        {/* <section id="carrierList" className="py-5 bg-light border-top">
          <Container>
            <Row>
              <Col>
                <InsuranceCarriersList_v01 />
              </Col>
            </Row>
          </Container>
        </section> */}

        {/* Footer */}
        <footer className="py-4">
          <Container>
            <Row className="align-items-center">
              <Col md={6} className="small text-body-secondary">
                © {new Date().getFullYear()} {BRAND.company}. All rights reserved.
                <div className="mt-2">
                  Insurance services subject to licensing and state availability. This page is for general information and not legal advice. {VERSION_NUMBER}
                </div>
              </Col>
              <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
                {AGENT_PHONE_BUTTON}
                <Button onClick={revealZipGate}>Get a Quote</Button>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

/* ============================================================
   SMALL UI BITS (reused)
============================================================ */
const Feature: React.FC<{ title: string; text: string; emoji: string }> = ({ title, text, emoji }) => (
  <Card className="h-100 shadow-sm">
    <Card.Body>
      <div className="display-6" aria-hidden>{emoji}</div>
      <h5 className="mt-3 mb-2">{title}</h5>
      <p className="mb-0 text-body-secondary">{text}</p>
    </Card.Body>
  </Card>
);

const StepCard: React.FC<{ n: number; title: string; text: string }> = ({ n, title, text }) => (
  <div className="d-flex align-items-start gap-3">
    <div className="badge rounded-pill text-bg-primary fs-6 mt-1">{n}</div>
    <div>
      <h6 className="mb-1">{title}</h6>
      <p className="mb-0 text-body-secondary">{text}</p>
    </div>
  </div>
);

/* ============================================================
   HERO SLIDES (placeholder images)
============================================================ */
interface Slide { src: string; alt: string; title: string; subline: string; }
const HERO_SLIDES: Slide[] = [
  { src: imagePath + "hero_contractor_01.jpg", alt: "Contractor on job site with safety gear", title: "Insurance Built for Contractors", subline: "GL, WC, Auto, Tools & more—quotes in minutes." },
  { src: imagePath + "hero_contractor_02.jpg", alt: "Team reviewing plans", title: "Win More Bids", subline: "Fast certificates and additional insureds when you need them." },
  { src: imagePath + "hero_contractor_03.jpg", alt: "Work truck and tools", title: "Flexible Coverage Options", subline: "We compare carriers so you don’t have to." },
];

/* ============================================================
   PAYLOAD BUILDER
============================================================ */
interface LeadPayload {
  // contact
  fullName: string;
  email: string;
  phone: string;
  state: string;
  zipCode: string;

  // business
  legalName?: string;
  trade?: string;
  entityType?: string;
  yearsInBusiness?: string | number;
  employees?: string | number;
  fein?: string;
  licenseNumber?: string;
  annualRevenue?: string;

  // coverage
  needGL?: boolean;
  needWC?: boolean;
  needCA?: boolean;
  needTools?: boolean;
  needBond?: boolean;
  needUmbrella?: boolean;
  aiReq?: boolean;
  certReq?: boolean;
  projType?: string;
  avgJobSize?: string;

  // loss/safety
  anyClaims?: boolean;
  claimsCount?: string | number;
  claimsNotes?: string;
  safetyProgram?: boolean;

  // misc
  notes?: string;
  utm?: { source?: string; medium?: string; campaign?: string };

  // meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

function buildPayload(values: Record<string, any>): LeadPayload {
  const urlParams = (typeof window !== "undefined") ? new URLSearchParams(window.location.search) : new URLSearchParams("");
  return {
    // contact
    fullName: (values.fullName || "").trim(),
    email: (values.email || "").trim(),
    phone: digitsOnly(values.phone || ""),
    state: String(values.state || ""),
    zipCode: String(values.zipCode || ""),

    // business
    legalName: values.legalName?.trim() || undefined,
    trade: values.trade || undefined,
    entityType: values.entityType || undefined,
    yearsInBusiness: values.yearsInBusiness ?? undefined,
    employees: values.employees ?? undefined,
    fein: values.fein?.trim() || undefined,
    licenseNumber: values.licenseNumber?.trim() || undefined,
    annualRevenue: values.annualRevenue?.trim() || undefined,

    // coverage
    needGL: !!values.needGL,
    needWC: !!values.needWC,
    needCA: !!values.needCA,
    needTools: !!values.needTools,
    needBond: !!values.needBond,
    needUmbrella: !!values.needUmbrella,
    aiReq: !!values.aiReq,
    certReq: !!values.certReq,
    projType: values.projType || undefined,
    avgJobSize: values.avgJobSize?.trim() || undefined,

    // loss
    anyClaims: !!values.anyClaims,
    claimsCount: values.anyClaims ? (values.claimsCount ?? undefined) : undefined,
    claimsNotes: values.anyClaims ? (values.claimsNotes?.trim() || undefined) : undefined,
    safetyProgram: !!values.safetyProgram,

    // misc
    notes: values.notes?.trim() || undefined,
    utm: {
      source: urlParams.get("utm_source") || undefined,
      medium: urlParams.get("utm_medium") || undefined,
      campaign: urlParams.get("utm_campaign") || undefined,
    },

    // meta
    domainName: (typeof window !== "undefined") ? String(window.location.hostname) : "",
    itemCategory: "Customer.contractor.quote",
    itemClass: "quote.request",
    itemType: "contractor.landingPage",
    clientVersion: String(VERSION_NUMBER),
  };
}

export default EASY_CONTRACTOR_LandingPage_v03a;
